import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  fetchSession,
  login as apiLogin,
  logout as apiLogout,
  oauthLogin as apiOAuth,
  register as apiRegister,
  setAuthExpiredHandler,
  type OAuthProvider,
  type Session,
} from '../api';

export type AuthModalMode = 'login' | 'register' | 'verify' | 'reset';

interface RegisterData {
  name: string;
  email: string;
  phone: string;
  password: string;
  captchaToken: string;
}

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<Session>;
  register: (data: RegisterData) => Promise<Session>;
  loginWithOAuth: (provider: OAuthProvider, token: string) => Promise<Session>;
  logout: () => Promise<void>;
  /** Replace the session locally — used after email verification / profile edits. */
  setSession: (session: Session | null) => void;
  refresh: () => Promise<void>;
  // Auth modal
  authModalOpen: boolean;
  authModalMode: AuthModalMode;
  openAuth: (mode?: AuthModalMode) => void;
  closeAuth: () => void;
  setAuthMode: (mode: AuthModalMode) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<AuthModalMode>('login');

  useEffect(() => {
    fetchSession().then((s) => {
      setSession(s);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    // The api layer has no view of React state, so it hands back the one signal
    // it can be sure of: a refresh that came back 401 means the session is over.
    // Passing the function straight through — `setAuthExpiredHandler` is a plain
    // registration function, not a state setter, so this is not the functional-
    // updater form.
    setAuthExpiredHandler(() => setSession(null));
    return () => setAuthExpiredHandler(null);
  }, []);

  const refresh = useCallback(async () => {
    setSession(await fetchSession());
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    const s = await apiLogin(identifier, password);
    setSession(s);
    return s;
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    const s = await apiRegister(data);
    setSession(s);
    return s;
  }, []);

  const loginWithOAuth = useCallback(async (provider: OAuthProvider, token: string) => {
    const s = await apiOAuth(provider, token);
    setSession(s);
    return s;
  }, []);

  const logout = useCallback(async () => {
    // Only trust the cleared state if the server confirmed it. Otherwise the
    // cookies are still in place, and showing a logged-out UI over a live
    // session is a real hazard on a shared device — re-read the truth instead.
    setSession((await apiLogout()) ? null : await fetchSession());
  }, []);

  const openAuth = useCallback((mode: AuthModalMode = 'login') => {
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  }, []);

  const closeAuth = useCallback(() => setAuthModalOpen(false), []);

  return (
    <AuthContext.Provider
      value={{
        session,
        loading,
        login,
        register,
        loginWithOAuth,
        logout,
        setSession,
        refresh,
        authModalOpen,
        authModalMode,
        openAuth,
        closeAuth,
        setAuthMode: setAuthModalMode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
